// W2: Intercept posthog-js SDK's internal warn/error console output and
// forward it to the Electron main-process loggy via the W1 forwardLog bridge.
// Per rule: stay-in-scope (ai.knowledge/global) — engineer scope freeze:
// errors+warnings only, env-gated debug. info/debug are NOT forwarded.
// Per rule: think-in-systems (ai.knowledge/global) — posthog-js has no public
// logger hook; wrapping console.warn/error and filtering by the SDK's own
// "[PostHog.js]" log prefix is the minimal-coupling interception point.

import type { PostHog } from 'posthog-js';
import { forwardLog } from '#~/logger/forward.js';

// posthog-js prepends this prefix to every internal log line (see
// posthog-js/src/utils/logger.ts in upstream). Filtering on this prefix
// is what keeps the wrapped console.warn/error from forwarding UNRELATED
// app warnings as if they were SDK output.
const POSTHOG_LOG_PREFIX = '[PostHog.js]';

// Idempotency gate per acceptance: a WeakSet keyed on the PostHog instance
// ensures re-initialization of PostHogManager never installs duplicate
// listeners (and therefore never double-forwards a single SDK log line).
const ATTACHED_INSTANCES = new WeakSet<PostHog>();

// Brand identifies our wrapper functions on the live console.warn / .error.
// Identity check (rather than a module-level boolean) makes wrapConsoleOnce
// resilient to handlers being swapped out from underneath us — which
// happens routinely in Jest's beforeEach when tests replace console.warn /
// console.error with fresh spies between cases. A module-level flag would
// short-circuit re-wrap and leave the fresh spies un-instrumented; an
// identity brand re-detects the swap and re-installs.
// Per rule: ai-code-requires-tests (ai.knowledge/global) — the brand check
// is the production-side fix for the test-isolation bug surfaced in QA
// iter-1; the test that exercises the swap is the regression guard.
const WRAPPED_BRAND: unique symbol = Symbol.for('iris.posthog.console.wrapped');

type WrappedConsoleFn = ((...args: unknown[]) => void) & {
  [WRAPPED_BRAND]?: true;
};

function isPostHogTagged(args: ReadonlyArray<unknown>): boolean {
  if (args.length === 0) {
    return false;
  }
  const first = args[0];
  return typeof first === 'string' && first.startsWith(POSTHOG_LOG_PREFIX);
}

// SDK call shape varies: sometimes (prefix, message), sometimes
// (prefix, message, payload), sometimes (prefix, payloadObj). Preserve the
// full SDK payload into context per acceptance #2.
function extractMessageAndContext(args: ReadonlyArray<unknown>): {
  message: string;
  context: Record<string, unknown> | undefined;
} {
  const rest = args.slice(1);
  const messageParts: string[] = [];
  let context: Record<string, unknown> | undefined;

  for (const arg of rest) {
    if (arg === null || arg === undefined) {
      continue;
    }
    const t = typeof arg;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      messageParts.push(String(arg));
    } else if (t === 'object') {
      // Last non-primitive wins; SDK rarely emits more than one payload object.
      context = arg as Record<string, unknown>;
    }
  }

  return {
    message: messageParts.length > 0 ? messageParts.join(' ') : POSTHOG_LOG_PREFIX,
    context
  };
}

// Wrap console.warn / console.error if the current handlers are not
// already OUR branded wrappers. "Once" here means once per live handler
// pair — a fresh handler (e.g. a Jest spy installed by beforeEach)
// drops the brand, so we re-wrap on the next attachLogInterception call.
// Original handlers (whatever they are at the moment of wrapping) are
// invoked AFTER forwarding so devtools UX and downstream spies still see
// the call.
function wrapConsoleOnce(): void {
  const liveWarn = console.warn as WrappedConsoleFn;
  const liveError = console.error as WrappedConsoleFn;
  if (liveWarn[WRAPPED_BRAND] && liveError[WRAPPED_BRAND]) {
    return;
  }

  const origWarn = console.warn.bind(console);
  const origError = console.error.bind(console);

  const wrappedWarn: WrappedConsoleFn = (...args: unknown[]): void => {
    if (isPostHogTagged(args)) {
      const { message, context } = extractMessageAndContext(args);
      // Fire-and-forget; forwardLog is `void` and never throws.
      // exactOptionalPropertyTypes: spread context only when defined so the
      // optional field is absent rather than explicitly undefined.
      forwardLog({
        source: 'posthog',
        level: 'warn',
        message,
        ...(context !== undefined && { context })
      });
    }
    origWarn(...args);
  };
  wrappedWarn[WRAPPED_BRAND] = true;
  console.warn = wrappedWarn;

  const wrappedError: WrappedConsoleFn = (...args: unknown[]): void => {
    if (isPostHogTagged(args)) {
      const { message, context } = extractMessageAndContext(args);
      forwardLog({
        source: 'posthog',
        level: 'error',
        message,
        ...(context !== undefined && { context })
      });
    }
    origError(...args);
  };
  wrappedError[WRAPPED_BRAND] = true;
  console.error = wrappedError;
}

// Attaches the interception layer to a PostHog instance. Idempotent per
// instance — second+ calls are no-ops, so PostHogManager.reset()/re-init
// never produces duplicate forwarded log lines.
export function attachLogInterception(posthog: PostHog): void {
  if (ATTACHED_INSTANCES.has(posthog)) {
    return;
  }
  ATTACHED_INSTANCES.add(posthog);
  wrapConsoleOnce();
}
